import { useEffect, useState } from "react";
import { Star, MapPin, Award, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

export default function Dealers() {
  const [dealers, setDealers] = useState([]);
  const [brand, setBrand] = useState("");
  const [loc, setLoc] = useState("");

  useEffect(() => {
    api.get("/dealers", { params: { brand: brand || undefined, location: loc || undefined } })
      .then((r) => setDealers(r.data))
      .catch(() => setDealers([]));
  }, [brand, loc]);

  const stars = (n) => Array.from({ length: 5 }, (_, i) => (
    <Star key={i} className={`w-3 h-3 ${i < Math.round(n) ? "fill-yellow-400 text-yellow-400" : "text-gray-700"}`} />
  ));

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 md:py-16" data-testid="dealers-page">
      <div className="flex items-center gap-2 mb-3"><Award className="w-4 h-4 text-silver" /><span className="label-mono">555 Dealer Network</span></div>
      <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-3">Vetted dealer ratings.</h1>
      <p className="text-gray-400 max-w-2xl mb-10">Our internal ratings — based on transparency, speed of execution, and customer satisfaction across hundreds of broker-led deals.</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-8 max-w-2xl">
        <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Filter by brand…" className="bg-[#0A0A0E] border-[#1A1A24] h-11 rounded-full text-white px-5" data-testid="dealers-brand-filter" />
        <Input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Location (city/state)…" className="bg-[#0A0A0E] border-[#1A1A24] h-11 rounded-full text-white px-5" data-testid="dealers-location-filter" />
      </div>

      {dealers.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl p-12 text-center text-gray-400">
          Our dealer ratings are being curated. Check back soon — or ask your broker directly which dealer they're using for your deal.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {dealers.map((d) => (
            <div key={d.id} className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6 hover-lift" data-testid={`dealer-card-${d.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-display text-xl">{d.name}</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{d.location}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl">{d.overall_rating.toFixed(1)}</div>
                  <div className="flex gap-0.5 justify-end">{stars(d.overall_rating)}</div>
                </div>
              </div>
              {d.brand_scope && <div className="label-mono mb-3">{d.brand_scope}</div>}
              <div className="space-y-2 text-xs">
                {[
                  ["Transparency", d.transparency],
                  ["Speed", d.speed],
                  ["Customer satisfaction", d.customer_satisfaction],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="flex justify-between mb-1"><span className="text-gray-400">{k}</span><span>{v.toFixed(1)}</span></div>
                    <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full silver-gradient" style={{ width: `${(v / 5) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              {d.notes && <p className="mt-4 text-xs text-gray-400 leading-relaxed">{d.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
